import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

//components
import withLocalization from '../../../hoc/withLocalization';
import applicationRouter from '../../../hoc/applicationRouter';
import CycleDetails from './CycleDetails';
import DayCard from './DayCard';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import CustomCheckbox from '~/components/CustomCheckbox/CustomCheckbox';

//utils
import { calculateCycleTotals, calculateWeeklyTotals, isDayFixedInOtherWeeks } from '~/utils/rosterMethods';
import { getWeekDays } from '~/utils/commonMethods';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';
import { workTypes } from '../../../utils/constants';


const getCurrentWeekTotals = (selectedWeekData, cycleTotals) => {
    const currentWeekTotal = cycleTotals.find(week => week.week == selectedWeekData.week);
    if (!currentWeekTotal || currentWeekTotal.shiftCount === 0) {
        return { totalHours: '-', shiftCount: '-' };
    }
    return {
        totalHours: currentWeekTotal.totalHours,
        shiftCount: currentWeekTotal.shiftCount
    };
}


class WeeklyShiftModal extends Component {
    constructor(props) {
        super(props);
    }

    handleDayToggle = (dayKey, dayDate) => {
        const { selectedWeekData, updateCycleData, cycleData } = this.props;
        const weekNumber = selectedWeekData?.week;
        const weekData = weekNumber ? (cycleData[weekNumber] || {}) : {};
        const newWeekData = {
            ...weekData,
            [dayKey]: {
                ...(weekData[dayKey] || {}),
                fixed: !weekData[dayKey]?.fixed,
                date: dayDate
            }
        };
        updateCycleData(weekNumber, newWeekData);
    }

    handleShiftSelect = (dayKey, shiftDetail, dayDate) => {
        const { selectedWeekData, updateCycleData, cycleData, commonStore, t } = this.props;
        const weekNumber = selectedWeekData?.week;
        const weekData = weekNumber ? (cycleData[weekNumber] || {}) : {};

        const companyShifts = commonStore.config.companyShifts || [];
        const shiftInfo = companyShifts.find(s => s.id === shiftDetail.id);
        const shiftType = shiftInfo?.shift_type;

        const field = shiftType === 'freeShift' ? 'fCodeId'
            : shiftType === 'standbyShift' ? 'standbyCodeId'
            : 'shiftCodeId';

        const newWeekData = { ...weekData };
        const currentDay = weekData[dayKey] || {};

        if (currentDay[field] === shiftDetail.id) {
            // Deselect: remove this field; delete the day entry if no shifts remain
            const { [field]: _removed, ...rest } = currentDay;
            if (rest.shiftCodeId || rest.fCodeId || rest.standbyCodeId) {
                newWeekData[dayKey] = rest;
            } else {
                delete newWeekData[dayKey];
            }
        } else {
            if (field === 'standbyCodeId' && shiftInfo?.work_type) {
                const conflictingDay = Object.entries(weekData).find(([otherDayKey, otherDayData]) => {
                    if (otherDayKey === dayKey || !otherDayData?.standbyCodeId) return false;
                    const otherShiftInfo = companyShifts.find(s => s.id === otherDayData.standbyCodeId);
                    return otherShiftInfo?.work_type && otherShiftInfo.work_type !== shiftInfo.work_type;
                });
                if (conflictingDay)
                    return commonStore.addNotification(t(`All standby shifts in a week must have the same work type (${workTypes.find(w => w.value !== shiftInfo.work_type)?.label})`), null, 'error');
            }
            newWeekData[dayKey] = { ...currentDay, [field]: shiftDetail.id, date: dayDate };
        }

        updateCycleData(weekNumber, newWeekData);
    }

    handleSelectAll = (checked) => {
        const { selectedWeekData, updateCycleData, cycleData } = this.props;
        const weekNumber = selectedWeekData?.week;
        const weekData = weekNumber ? (cycleData[weekNumber] || {}) : {};
        const weekDays = selectedWeekData?.days || [];

        const newWeekData = { ...weekData };
        weekDays.forEach(day => {
            newWeekData[day.key] = {
                ...(newWeekData[day.key] || {}),
                fixed: checked,
                date: day.date
            };
        });
        updateCycleData(weekNumber, newWeekData);
    }

    handleSaveWeeklyShiftCode = () => {
        const { generateEventsFromCycleData, cycleData, handleHideShiftCodeModal, commonStore,
            user, t, allowSaveShifts, onAllowSaveShiftsChange, selectedWeekData } = this.props;

        const currentWeekNumber = selectedWeekData?.week;
        const currentWeekData = cycleData[currentWeekNumber] || {};

        let currentWeekHours = 0;
        Object.values(currentWeekData).forEach(dayData => {
            const { shiftCodeId } = dayData;
            if (!shiftCodeId) return;
            const shiftInfo = commonStore.getCompanyShift(shiftCodeId);
            if (shiftInfo) {
                currentWeekHours += shiftInfo.normal_work_day;
            }
        });

        const employement_percent_hours = (user.employee_percent * user.work_time_agreement) / 100;

        if ((currentWeekHours / 60) > employement_percent_hours && !allowSaveShifts) {
            confirmAlert({
                title: t('Are you sure?'),
                message: t(`You have assigned more hours to this employee than their contract allows for this week. Do you want to continue`) + '?',
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: () => {
                            handleHideShiftCodeModal();
                            onAllowSaveShiftsChange(true);
                            generateEventsFromCycleData(cycleData);
                        },
                    },
                    {
                        label: t('No'),
                        onClick: () => {
                            onAllowSaveShiftsChange(false);
                        }
                    }
                ],
            });
        } else {
            handleHideShiftCodeModal();
            generateEventsFromCycleData(cycleData);
        }
    }

    handlePrevWeek = () => {
        const { selectedWeekData, cycleDetails } = this.props;
        if (!selectedWeekData) return;

        if (moment(cycleDetails.startDate).isSame(selectedWeekData.start)) {
            return;
        }

        const prevWeekStart = moment(selectedWeekData.start).subtract(1, 'weeks');
        this.updateWeekData(prevWeekStart);
    }

    handleNextWeek = () => {
        const { selectedWeekData, cycleDetails } = this.props;
        if (!selectedWeekData) return;

        if (moment(cycleDetails.endDate).isSame(selectedWeekData.end)) {
            return;
        }

        const nextWeekStart = moment(selectedWeekData.start).add(1, 'weeks');
        this.updateWeekData(nextWeekStart);
    }

    updateWeekData = (targetDate) => {
        const { cycleDetails, updateSelectedWeekData } = this.props;
        const date = moment(targetDate);
        const weekNumber = date.isoWeek();
        let weekStart = date.clone().startOf('isoWeek').format('YYYY-MM-DD');
        let weekEnd = date.clone().endOf('isoWeek').format('YYYY-MM-DD');

        const cycleStart = moment(cycleDetails.startDate);
        const cycleEnd = moment(cycleDetails.endDate);

        if (!moment(weekStart).isBetween(cycleStart, cycleEnd, "date", '[]')) {
            weekStart = cycleStart.format('YYYY-MM-DD');
        }

        if (!moment(weekEnd).isBetween(cycleStart, cycleEnd, "date", '[]')) {
            weekEnd = cycleEnd.format('YYYY-MM-DD');
        }

        const weekData = {
            week: weekNumber,
            start: weekStart,
            end: weekEnd,
            days: getWeekDays(weekStart, weekEnd, 'DD.MM.YYYY')
        };

        if (updateSelectedWeekData) {
            updateSelectedWeekData(weekData);
        }
        return weekData;
    }

    handleWeekNavigation = (weekNumber) => {
        const { cycleDetails } = this.props;
        if (!cycleDetails) return;

        const cycleStart = moment(cycleDetails.startDate);
        const cycleEnd = moment(cycleDetails.endDate);
        const targetWeekNumber = parseInt(weekNumber);

        let currentWeekStart = cycleStart.clone().startOf('isoWeek');
        let targetDate = null;

        while (currentWeekStart.isSameOrBefore(cycleEnd)) {
            if (currentWeekStart.isoWeek() === targetWeekNumber) {
                const weekEnd = currentWeekStart.clone().endOf('isoWeek');
                if (currentWeekStart.isSameOrBefore(cycleEnd) && weekEnd.isSameOrAfter(cycleStart)) {
                    targetDate = currentWeekStart.clone();
                    break;
                }
            }
            currentWeekStart.add(1, 'week');
        }

        if (!targetDate) {
            currentWeekStart = cycleStart.clone().add(1, 'year').startOf('year').startOf('isoWeek');
            const nextYearEnd = cycleEnd.clone().add(1, 'year');

            while (currentWeekStart.isSameOrBefore(nextYearEnd)) {
                if (currentWeekStart.isoWeek() === targetWeekNumber) {
                    const weekEnd = currentWeekStart.clone().endOf('isoWeek');
                    if (currentWeekStart.isSameOrBefore(cycleEnd) && weekEnd.isSameOrAfter(cycleStart)) {
                        targetDate = currentWeekStart.clone();
                        break;
                    }
                }
                currentWeekStart.add(1, 'week');
            }
        }

        if (targetDate) {
            this.updateWeekData(targetDate);
        } else {
            console.warn(`Week ${targetWeekNumber} not found within cycle boundaries`);
        }
    }

    render() {
        const { commonStore, showShiftCodeModal, handleHideShiftCodeModal, selectedWeekData, cycleData, t, isRosterPublished, cycleDetails, user, userWorkHoursRuleByDepartment, disableShiftsForDepartmentChanges, exceptions, approvedF5Dates, f5Placements } = this.props;
        if (!showShiftCodeModal || !selectedWeekData) return null;

        const { mandatory_overtime_limit_per_week = 0 } = userWorkHoursRuleByDepartment;
        const allowStandbyCalls = !!(commonStore.config?.client?.data?.basicRules?.urgentWorkSettings?.allowStandbyCalls);
        const weekDays = selectedWeekData?.days || [];
        const allConditionalShifts = commonStore.sortCompanyShifts(commonStore.config.companyShifts, false, { F1: false, F2: false, F3: true, F4: true, F5: true }, true);
        const baseShifts = allConditionalShifts.filter(s => !['F3', 'F4', 'F5'].includes(s.short_code));
        const exceptionKeys = Object.keys(exceptions || {});
        const weekNumber = selectedWeekData?.week;
        const weekData = weekNumber ? (cycleData[weekNumber] || {}) : {};
        const dateTimeRules = commonStore.getDateTimeRules();
        const cycleTotals = calculateWeeklyTotals(cycleData, commonStore, cycleDetails);
        const currentWeekTotals = getCurrentWeekTotals(selectedWeekData, cycleTotals);
        const cycleTotalsSummary = calculateCycleTotals(cycleTotals, cycleDetails);
        const calculatedEmployementHours = (user.employee_percent * user.work_time_agreement) / 100;
        const totalLimitAccordingToCycleLength = (calculatedEmployementHours * userWorkHoursRuleByDepartment.cycle_week_length).toFixed(dateTimeRules.decimal_places || 2);
        const maxCycleLimitBalance = (totalLimitAccordingToCycleLength - parseFloat(cycleTotalsSummary.totalHours)).toFixed(dateTimeRules.decimal_places || 2);

        const F5_EXPIRY_DAYS = 30;
        const f5ShiftId = allConditionalShifts.find(s => s.short_code === 'F5')?.id;

        const cycleDaysByDate = {};
        for (const wkData of Object.values(cycleData)) {
            for (const dayData of Object.values(wkData)) {
                if (dayData?.date) {
                    cycleDaysByDate[moment(dayData.date, 'DD.MM.YYYY').format('YYYY-MM-DD')] = dayData;
                }
            }
        }

        const f5PlacedTriggerDates = new Set();
        const f5ActivelyPlacedInWindow = new Set();
        if (f5ShiftId) {
            for (const { triggerDate, windowStart, windowEnd, placementDate } of (f5Placements || [])) {
                if (!triggerDate) continue;
                // Check live cycleData for F5 in this window (primary or secondary)
                for (const [date, dayData] of Object.entries(cycleDaysByDate)) {
                    if (date < windowStart || date > windowEnd) continue;
                    if (dayData.shiftCodeId === f5ShiftId || dayData.fCodeId === f5ShiftId) {
                        f5ActivelyPlacedInWindow.add(triggerDate);
                        f5PlacedTriggerDates.add(triggerDate);
                        break;
                    }
                }
                if (placementDate) f5PlacedTriggerDates.add(triggerDate);
            }
        }

        const today = moment().format('YYYY-MM-DD');
        const f5TriggerWindows = (f5Placements || [])
            .filter(({ triggerDate, windowStart, windowEnd }) => triggerDate && windowStart && windowEnd)
            .filter(({ triggerDate, windowEnd }) =>
                today <= windowEnd ||
                f5PlacedTriggerDates.has(triggerDate) ||
                exceptionKeys.some(k => k.startsWith(`${triggerDate}_f5_`))
            );

        return (
            <Modal size="xl" centered show={showShiftCodeModal} onHide={handleHideShiftCodeModal}>
                <Modal.Header>
                    <Modal.Title className="w-100 text-center">
                        <div className="d-flex justify-content-between align-items-center px-3 text-white">
                            <div></div>

                            <h5 className="mb-0 fw-bold text-light">{`${t('Week')} ${selectedWeekData.week} (${moment(selectedWeekData.start).format(dateTimeRules.short_date_format)} | ${moment(selectedWeekData.end).format(dateTimeRules.short_date_format)})`}</h5>

                            <div className="d-flex align-items-center gap-2">
                                <span className="me-2 fw-bold">{t('Select')} {t('All')} {t('Days')}</span>
                                <div className="weekly-shift-select-all-checkbox">
                                    <CustomCheckbox
                                        isChecked={weekDays.every(day => {
                                            const isFixedInOtherWeeks = isDayFixedInOtherWeeks(day.key, cycleData, weekNumber);
                                            return isFixedInOtherWeeks || weekData[day.key]?.fixed;
                                        })}
                                        onChange={(checked) => this.handleSelectAll(checked)}
                                        disabled={isRosterPublished || weekDays.some(day => isDayFixedInOtherWeeks(day.key, cycleData, weekNumber)) ||
                                            !weekDays.every(day => weekData[day.key]?.shiftCodeId)
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </Modal.Title>
                    <img
                        src={CloseModalIcon}
                        className="cursor-pointer"
                        alt='close icon'
                        onClick={handleHideShiftCodeModal}
                    />
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80dvh' }}>
                    <div className="d-flex">

                        <div className="d-flex" style={{ alignItems: 'flex-start' }}>

                            <div className="weekly-shift-sidebar">
                                <div className="weekly-shift-current-week">
                                    <div className="fw-bold">{t('Current Week')}</div>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="text-muted">
                                            <span className="fw-bold">{t('shifts')}:</span> {currentWeekTotals.shiftCount}
                                        </div>
                                        <div className="text-muted">
                                            <span className="fw-bold">{t('Hours')}:</span> {currentWeekTotals.totalHours}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ borderRight: '1px solid #dee2e6', height: '100%', paddingRight: '10px' }}>
                                    <CycleDetails
                                        cycleTotalsSummary={cycleTotalsSummary}
                                        cycleTotals={cycleTotals}
                                        selectedWeek={selectedWeekData}
                                        workHoursRuleByDepartment={userWorkHoursRuleByDepartment}
                                        mandatory_overtime_limit_per_week={mandatory_overtime_limit_per_week}
                                        handleWeekNavigation={this.handleWeekNavigation}
                                    />
                                </div>
                            </div>

                        </div>

                        <div className="weekly-shift-main-content">
                            <div className="d-flex justify-content-between align-items-center mb-2 px-3">
                                <div>{t(`Total Cycle Hours`)}: {totalLimitAccordingToCycleLength}{t('hrs')} | {t(`Hours Left`)}: <span className={maxCycleLimitBalance < 0 ? 'text-danger fw-600' : ''}>{maxCycleLimitBalance}{t('hrs')}</span></div>

                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        fill dynamic_lg
                                        onClick={this.handlePrevWeek}
                                        disabled={moment(cycleDetails.startDate).isSame(selectedWeekData.start)}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                        <span className="ms-2">{t('Previous')}</span>
                                    </Button>
                                    <Button
                                        fill dynamic_lg
                                        onClick={this.handleNextWeek}
                                        disabled={moment(cycleDetails.endDate).isSame(selectedWeekData.end)}
                                    >
                                        <span className="me-2">{t('Next')}</span>
                                        <i className="fas fa-chevron-right"></i>
                                    </Button>
                                </div>
                            </div>

                            <div className="modal-mask">
                                <div className="modal-container">
                                    <div className="container-fluid py-3" style={{ overflow: 'auto', maxHeight: '65dvh' }}>
                                        <div className="row">
                                            {weekDays.map((day) => {
                                                const isFixedInOtherWeeks = isDayFixedInOtherWeeks(day.key, cycleData, weekNumber);
                                                const isFixedInThisWeek = weekData[day.key]?.fixed;
                                                const dayDate = day.date;
                                                const formattedDate = moment(dayDate, 'DD.MM.YYYY');
                                                const isDepartmentChange = disableShiftsForDepartmentChanges && formattedDate.isSameOrBefore(moment(disableShiftsForDepartmentChanges), 'day')
                                                const dayDateFormatted = formattedDate.format('YYYY-MM-DD');
                                                const isHoliday = commonStore.isHoliday(dayDateFormatted);
                                                const holidayDetails = isHoliday ? commonStore.getHolidayDetails(dayDateFormatted) : null;
                                                const dayLabel = `${t((day?.dayName?.slice(0, 3)))} (${formattedDate.format(dateTimeRules.short_date_format)})`;
                                                const currentDayShiftId = weekData[day.key]?.shiftCodeId;
                                                const currentDayShift = currentDayShiftId
                                                    ? allConditionalShifts.find(s => s.id === currentDayShiftId)
                                                    : null;
                                                const hasF3 = currentDayShift?.short_code === 'F3' || exceptionKeys.some(k => k.endsWith(`date_to_add_f3_${dayDateFormatted}`));
                                                const hasF4 = currentDayShift?.short_code === 'F4' || exceptionKeys.some(k => k.endsWith(`date_to_add_f4_${dayDateFormatted}`));

                                                const currentDayFCodeShift = weekData[day.key]?.fCodeId
                                                    ? allConditionalShifts.find(s => s.id === weekData[day.key].fCodeId)
                                                    : null;
                                                const currentDayHasF5 = currentDayShift?.short_code === 'F5' || currentDayFCodeShift?.short_code === 'F5';

                                                const isWeekday = formattedDate.day() >= 1 && formattedDate.day() <= 5;
                                                const hasF5 = currentDayHasF5 ||
                                                    exceptionKeys.some(k => k.endsWith(`date_to_add_f5_${dayDateFormatted}`)) ||
                                                    (isWeekday && !isHoliday && f5TriggerWindows.some(({ triggerDate, windowStart, windowEnd }) =>
                                                        dayDateFormatted >= windowStart &&
                                                        dayDateFormatted <= windowEnd &&
                                                        !f5ActivelyPlacedInWindow.has(triggerDate)
                                                    ));

                                                const isApprovedF5Day = !!approvedF5Dates?.has(dayDateFormatted);
                                                const isDisabled = (isFixedInOtherWeeks && !isFixedInThisWeek) || !!isDepartmentChange || isApprovedF5Day;

                                                const dayCompanyShifts = (hasF3 || hasF4 || hasF5)
                                                    ? allConditionalShifts.filter(s =>
                                                        (s.short_code !== 'F3' || hasF3) &&
                                                        (s.short_code !== 'F4' || hasF4) &&
                                                        (s.short_code !== 'F5' || hasF5)
                                                    )
                                                    : baseShifts;

                                                return (
                                                    <DayCard
                                                        day={day}
                                                        dayDate={dayDate}
                                                        dayLabel={dayLabel}
                                                        isFixedInOtherWeeks={isFixedInOtherWeeks}
                                                        isFixedInThisWeek={isFixedInThisWeek}
                                                        isHoliday={isHoliday}
                                                        isRosterPublished={isRosterPublished}
                                                        holidayDetails={holidayDetails}
                                                        isDisabled={isDisabled}
                                                        weekData={weekData}
                                                        companyShifts={dayCompanyShifts}
                                                        handleShiftSelect={this.handleShiftSelect}
                                                        handleDayToggle={this.handleDayToggle}
                                                        allowStandbyCalls={allowStandbyCalls}
                                                        addHelperText={
                                                            (isDepartmentChange && t("This day's shifts are locked due to a department change.")) ||
                                                            (isApprovedF5Day && t('This day is locked — it has been assigned as an F5 replacement rest day.'))
                                                        }
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className='justify-content-between'>
                    <div className='d-flex align-item-center'>
                        <i class="mt-1 me-1 fas fa-exclamation-circle"></i>
                        <div><strong>WS</strong> - {t(`Working Shift`)}</div>
                        <div>, <strong>FC</strong> - {t('Free')} {t('Shift')}</div>
                        {allowStandbyCalls ? <div>, <strong>SB</strong> - {t('Standby')} {t('Shift')}</div> : null}
                    </div>
                    <div>
                        <Button className='me-2' variant="outline-danger" onClick={handleHideShiftCodeModal}>{t('Cancel')}</Button>
                        <Button fill dynamic_lg onClick={this.handleSaveWeeklyShiftCode}>{t('Save')}</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default inject('userStore', 'commonStore', 'scheduleStore', 'clientStore', 'rosterStore')(withLocalization(applicationRouter(observer(WeeklyShiftModal))));